import { catchError, from, mergeMap, of, tap } from "rxjs"
import {
  googleAccessTokenSignal,
  consentShownSignal,
  hasGrantedPermissions,
  getTokenExpirationDate,
  requestGoogleAccessToken,
} from "../../auth"
import { Logger } from "../../../../debug/logger.shared"
import { useGoogleScripts } from "./scripts"
import { CancelError } from "../../../../errors/errors.shared"
import { useConfig } from "../../../../config/useConfig"
import { hasMinimumValidityLeft } from "../../../plugins/common/tokenValidity"

const isPopupClosedError = (error: unknown) => {
  return (
    error &&
    typeof error === "object" &&
    "type" in error &&
    error.type === "popup_closed"
  )
}

export const useRequestToken = ({
  requestPopup,
}: {
  requestPopup: () => Promise<boolean>
}) => {
  const { data: config } = useConfig()
  const { getGoogleScripts } = useGoogleScripts()

  const requestToken = ({ scope }: { scope: string[] }) =>
    getGoogleScripts().pipe(
      mergeMap(([gsi]) => {
        const firstScope = scope[0]
        const accessToken = googleAccessTokenSignal.getValue()
        const minimumValidityMs = config?.MINIMUM_TOKEN_VALIDITY_MS

        /**
         * We return current token if it has enough time left for downstream
         * requests, based on the shared web configuration threshold, and the
         * scope is valid.
         */
        if (accessToken && minimumValidityMs !== undefined) {
          if (
            (!firstScope ||
              (firstScope && hasGrantedPermissions(gsi, accessToken, scope))) &&
            hasMinimumValidityLeft({
              expiresAt: getTokenExpirationDate(accessToken),
              minimumValidityMs,
            })
          ) {
            return of(accessToken)
          }
        }

        Logger.info(
          `google token invalid or below ${minimumValidityMs}ms validity, requesting new one`,
        )

        return from(requestPopup()).pipe(
          tap((confirmed) => {
            if (!confirmed) throw new CancelError()
          }),
          mergeMap(() => {
            consentShownSignal.update(true)

            return from(
              requestGoogleAccessToken(
                gsi,
                scope,
                config?.GOOGLE_CLIENT_ID ?? "",
              ),
            ).pipe(
              mergeMap((accessToken) => {
                consentShownSignal.update(false)

                if (accessToken.error) {
                  Logger.error(`google access token error`, accessToken)

                  throw new Error(accessToken.error)
                }

                if (!hasGrantedPermissions(gsi, accessToken, scope)) {
                  throw new Error("not enough permissions")
                }

                const googleAccessToken = {
                  ...accessToken,
                  created_at: Date.now(),
                }

                googleAccessTokenSignal.update(googleAccessToken)

                return of(googleAccessToken)
              }),
            )
          }),
        )
      }),
      catchError((e) => {
        consentShownSignal.update(false)

        if (isPopupClosedError(e)) {
          throw new CancelError()
        }

        throw e
      }),
    )

  return { requestToken }
}
